




































































































































import { Vue, Component, Prop, Ref } from "vue-property-decorator";

import {
  ContactPersonInfo,
  ProjectConclusionState,
  ProjectDetails,
} from "@/data/models/ProjectDetails";
import Report from "@/data/models/Report";
import { storage } from "@/main";
import { ClientPanelAssignment } from "@/data/models/ClientPanelAssignment";
import { firestore } from "firebase/app";
import { formatDateShort, formatDurationLong, timestampToDate } from "@/utils/DateFormatter";
import { nullIfUndefined } from "@/utils/Utils";
import { getIndependantDeviceProjectAssignments } from "@/data/models/DeviceProjectAssignment";

import { getClientPanel } from "@/data/repository/ClientPanelRepository";
import {
  saveFinishedProject,
  reopenProject,
} from "@/data/repository/ProjectRepository";
import { updateSyncTargets } from "@/data/repository/ProjectStatusRepository";

import { createHyDryCertificateForProject, createPdfReportForProject } from "@/utils/pdf/PdfCreator";

import { ReportUsage, ReportType } from "@/data/models/Report";

import ProjectDevicesComponent from "@/components/project/devices/ProjectDevices.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import clone from "clone";

import { ButtonState } from "@/components/common/StateButton.vue";
import {
getActiveUser,
  getActiveUserId,
  getRecordInfo,
} from "@/data/repository/UserRepository";
import ClientDataSectionComponent from "@/components/project/clientdata/ClientDataSection.vue";
import ClientPanel from "@/data/models/ClientPanel";
import { ProjectDetailsTab } from "@/data/models/ProjectDetailsTab";
import { isSimulatingErp } from "@/data/repository/ClientRepository";
import { GenericDeviceProjectAssignment } from "@/data/models/GenericDeviceAssignment";
import { alwaysAllowedToConcludeProjectAccess } from "@/permissions/UserPermissions";
import { getProjectSettingsV2, ProjectSettingsOptions } from "@/data/models/Settings";
import { ProjectManagementSettingsModule } from "@/store/modules/ProjectManagementSettingsModule";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK, COLOR_BUTTON_RED } from "@/utils/colors";
import { themed, themedFill } from "@/utils/Theme";

@Component({
  components: {
    "client-data-section": ClientDataSectionComponent,
    "project-devices": ProjectDevicesComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class ConcludeProjectComponent extends Vue {
  @Prop()
  projectDetails!: ProjectDetails;

  @Prop()
  clientPanelAssignments!: Array<ClientPanelAssignment>;

  @Prop()
  genericDeviceAssignments!: Array<GenericDeviceProjectAssignment>;

  @Ref("confirm")
  confirmationDialogComponent!: ConfirmationDialogComponent;

  @Prop({ default: false })
  isSavingProject!: boolean;

  @Prop({ default: true })
  canEditProject!: boolean;

  @Prop({ default: false })
  hasEditProjectAccess!: boolean;

  @Prop({ default: false })
  isReadOnlyMode!: boolean;

  @Prop({ default: false })
  isDryingWorkflowInProgress!: boolean;

  @Prop()
  reports!: Report[];

  tab = ProjectDetailsTab.CONCLUDE_PROJECT;

  projectStartDate: null | firestore.Timestamp = null;
  projectEndDate: null | firestore.Timestamp = null;

  showDeviceEditOptions = false;

  isCreatingReport = false;
  isSiningOffDevices = false;
  successSnackbarTimeout = 5000;
  showAllDevicesSignedOffSnackbar = false;
  showProjectConcludedSnackbar = false;
  showConcludeDialog = false;
  concludeDialogMessage = "";

  isConcludingProject = false;

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /** An action: the brand red, the accent in dark mode (its white label stays). */
  get concludeDialogButtonColor() {
    return themedFill(COLOR_BUTTON_RED);
  }

  get activeUser() {
    return getActiveUser();
  }

  get isAlwaysAllowedToConcludeProject() {
    return alwaysAllowedToConcludeProjectAccess(this.activeUser);
  }

  get projectDuration() {
    const projectStartDate = this.projectDetails.clientData.projectStartDate;

    const formattedStartDate = formatDateShort(
      projectStartDate
    );

    const projectEndDate = this.projectDetails.projectConclusionData
      ?.projectConclusionDate;

    let projectDuration = "";
    let endDate = new Date();

    if (!projectEndDate) {
      projectDuration = this.$t("common.since", [
        formattedStartDate,
      ]).toString();
    } else {
      const startYear = timestampToDate(projectStartDate)?.getFullYear();
      const endYear = timestampToDate(projectEndDate)?.getFullYear();

      const formattedEndDate = projectEndDate
        ? formatDateShort(projectEndDate, undefined, undefined, startYear !== endYear)
        : "-";

      projectDuration = this.$t("common.fromUntil", [
        formattedStartDate,
        formattedEndDate,
      ]).toString();

      endDate = projectEndDate.toDate();
    }

    const timeDiffInDays = Math.ceil(
      (endDate.getTime() -
        this.projectDetails.clientData.projectStartDate.toDate().getTime()) /
        (1000 * 60 * 60 * 24)
    );

    const timeDiffInMinutes = Math.max(24 * 60, timeDiffInDays * 60 * 24);

    return (
      projectDuration +
      " (" +
      formatDurationLong(this, timeDiffInMinutes, false) +
      ")"
    );
  }

  get clientPanelsNotSignedOff() {
    return this.clientPanelAssignments.filter(
      (clientPanelAssignment) => !clientPanelAssignment.assignmentEndDate
    );
  }

  get hasFinalReport() {
    return this.reports.some(
      (report) => report.reportType === ReportType.FINAL_REPORT
    );
  }

  get createInterimButtonState() {
    if (this.isProjectFinished) {
      return this.hasFinalReport ? ButtonState.DISABLED : ButtonState.RED;
    }
    if (this.isReadOnlyMode) {
      return ButtonState.DISABLED;
    }
    return ButtonState.RED;
  }

  get isProjectFinished() {
    return !!this.projectDetails.projectConclusionData?.projectConclusionDate;
  }

  get isRestoredProject() {
    return this.projectDetails.conclusion?.conclusionState === ProjectConclusionState.RESTORED;
  }

  get showReleaseFinalReportButton() {
    //Disabled for now
    return false;
  }

  get releaseFinalReportButtonState() {
    const didReleaseFinalReport = this.reports.some(
      (report) =>
        report.reportType === ReportType.FINAL_REPORT && !!report.lastTimeSend
    );

    return didReleaseFinalReport ? ButtonState.GREEN : ButtonState.YELLOW;
  }

  get hasGenericDevices() {
    return this.genericDeviceAssignments.length > 0;
  }

  get showSignOfAllDevicesButton() {
    return this.canEditProject && this.clientPanelAssignments.length > 0;
  }

  get showApartmentNames() {
    return this.projectDetails.clientData.contactPersons.length > 1;
  }

  get projectManagementSettings() {
    return ProjectManagementSettingsModule.projectManagementSettings;
  }

  get projectSettings() {
    return getProjectSettingsV2(this.projectManagementSettings);
  }

  get canFinaliseProjectInPortal() {
    return this.projectSettings.finaliseProjectsIn.includes(ProjectSettingsOptions.PORTAL);
  }

  get concludeProjectButtonState() {
    if (this.isReadOnlyMode && !this.isProjectFinished) {
      return ButtonState.DISABLED;
    }

    if (this.isProjectFinished && this.hasEditProjectAccess) {
      return ButtonState.RED;
    }

    if (!this.canFinaliseProjectInPortal || !this.canEditProject) {
      return ButtonState.DISABLED;
    }

    if (this.isDryingWorkflowInProgress) {
      return ButtonState.DISABLED;
    }

    if (this.isReadOnlyMode && !this.isAlwaysAllowedToConcludeProject) {
      return ButtonState.DISABLED;
    }

    return this.areAllDevicesSignedOff || this.isProjectFinished
      ? ButtonState.RED
      : ButtonState.DISABLED;
  }

  get allUnsignedAssignments() {
    return [
      ...this.clientPanelsNotSignedOff,
      ...this.genericDevicesNotReleased
    ];
  }

  get hasUnsignedDependantAssignments() {
    return this.allUnsignedAssignments.some(
      (assignment) => !!assignment.externalElectricityMeterInfo
    );
  }

  get signOffAllDevicesButtonState() {
    return this.areAllDevicesSignedOff || this.isProjectFinished || this.hasUnsignedDependantAssignments
      ? ButtonState.DISABLED
      : ButtonState.YELLOW;
  }

  get genericDevicesNotReleased() {
    return this.genericDeviceAssignments.filter(
      (assignment) => !assignment.assignmentEndDate
    );
  }

  get areAllDevicesSignedOff() {
    return this.clientPanelsNotSignedOff.length === 0 && this.genericDevicesNotReleased.length === 0;
  }

  mounted() {
    this.projectStartDate = this.projectDetails.clientData.projectStartDate;
    this.projectEndDate = nullIfUndefined(
      this.projectDetails.projectConclusionData?.projectConclusionDate
    );
  }

  async onReleaseFinalReport() {
    this.isCreatingReport = true;
    
    const reportDate = new Date();
    const timestamp = firestore.Timestamp.fromDate(reportDate);

    const reportType = ReportType.FINAL_REPORT;
    
    const finalReport = await createPdfReportForProject(
      this,
      this.projectDetails,
      reportType,
      ReportUsage.INTERNAL,
      timestamp,
      getActiveUserId()
    );

    await this.openReport(finalReport);

    this.isCreatingReport = false;
  }

  async onCreateReport() {
    this.isCreatingReport = true;
    
    const reportDate = new Date();
    const timestamp = firestore.Timestamp.fromDate(reportDate);

    const reportType = this.isProjectFinished
      ? ReportType.FINAL_REPORT
      : ReportType.INTERIM_REPORT;

    const finalReport = await createPdfReportForProject(
      this,
      this.projectDetails,
      reportType,
      ReportUsage.INTERNAL,
      timestamp
    );

    await this.openReport(finalReport);

    this.isCreatingReport = false;
  }

  calculateReopenProjectDialogMessage() {
    const isRestoredProject = this.isRestoredProject;

    return isRestoredProject 
      ? this.$t("clientData.moveProjectToRunningDialog.messageForRestoredProjects").toString()
      : this.$t("clientData.moveProjectToRunningDialog.message").toString()
  }

  async onConcludeProject() {
    if (this.isProjectFinished) {
      const projectName = isSimulatingErp() ? this.projectDetails.clientData.projectNumberIres : this.projectDetails.clientData.projectName ??
        this.projectDetails.clientData.projectNumber;

      const result = await this.confirmationDialogComponent.show({
        title: this.$t("clientData.moveProjectToRunningDialog.title", [projectName]).toString(),
        message: this.calculateReopenProjectDialogMessage(),
        rejectMessage: this.$t("common.back").toString(),
        acceptMessage: this.$t("clientData.moveProjectToRunningDialog.acceptButtonText").toString(),
      });

      if (result) {
        this.isConcludingProject = true;
        const update = await reopenProject(clone(this.projectDetails), { isProjectFinished: this.isReadOnlyMode });
        this.isConcludingProject = false;

        const projectDetails = this.projectDetails;
        if (projectDetails.syncTarget) {
          await updateSyncTargets(projectDetails.id, projectDetails.syncTarget, true, false);
        }

        this.$emit("projectReopened", update);
      }
    } else {
      const projectName = isSimulatingErp()
        ? this.projectDetails.clientData.projectNumberIres
        : this.projectDetails.clientData.projectName ?? this.projectDetails.clientData.projectNumber;

      this.concludeDialogMessage = this.$t("projectConclusion.concludeProjectDialogMessage", [projectName]).toString();
      this.showConcludeDialog = true;
    }
  }

  onConcludeCancel() {
    this.showConcludeDialog = false;
  }

  async onConcludeWithoutReport() {
    this.showConcludeDialog = false;
    await this.concludeProject(false);
  }

  async onConcludeWithReport() {
    this.showConcludeDialog = false;
    await this.concludeProject(true);
  }

  async concludeProject(withReport: boolean) {
    this.isConcludingProject = true;

    const endDate = new Date();
    const endDateTimestamp = firestore.Timestamp.fromDate(endDate);

    const recordInfo = getRecordInfo();
    const finishedProject = clone(this.projectDetails);
    const projectConclusionData = {
      ...(finishedProject.projectConclusionData ?? {}),
      repairAction: null,
      dryingSuccessful: true,
      startedBy:
        finishedProject.projectConclusionData?.startedBy ?? recordInfo,
      finishedBy: recordInfo,
      projectConclusionDate: endDateTimestamp,
      complete: true,
    };

    finishedProject.projectConclusionData = projectConclusionData;

    let finalReport: Report | null = null;
    let hyDryCertificate: Report | null = null;

    if (withReport) {
      const [report, certificate] = await Promise.all([
        createPdfReportForProject(
          this,
          finishedProject,
          ReportType.FINAL_REPORT,
          ReportUsage.EXTERNAL,
          endDateTimestamp,
          recordInfo.recordedByUserId
        ),
        createHyDryCertificateForProject(
          this,
          finishedProject,
          endDateTimestamp,
          recordInfo.recordedByUserId
        )
      ]);
      finalReport = report;
      hyDryCertificate = certificate;
    }

    try {
      await saveFinishedProject(
        finishedProject,
        endDateTimestamp,
        finalReport,
        hyDryCertificate
      );
    }
    catch {
      // Skip
    }

    const syncTarget = this.projectDetails.syncTarget;
    if (syncTarget) {
      await updateSyncTargets(this.projectDetails.id, syncTarget, true, false);
    }

    this.$emit("projectConcluded", finishedProject);
    this.showProjectConcludedSnackbar = true;

    this.isConcludingProject = false;

    if (withReport && finalReport) {
      await this.openReports([
        finalReport.storageLocation,
        hyDryCertificate ? hyDryCertificate.storageLocation : ""
      ]);
    }
  }

  async openReport(report: Report) {
    const downloadUrl = await storage
      .ref(report.storageLocation)
      .getDownloadURL();

    window.open(downloadUrl, "_blank");
  }

  async openReports(storageLocations: string[]) {
    for (const storageLocation of storageLocations) {
      if (storageLocation === "") {
        continue;
      }

      const downloadUrl = await storage
        .ref(storageLocation)
        .getDownloadURL();

      window.open(downloadUrl, "_blank");
    }
  }

  async onSignOffAllDevices() {
    const result = await this.confirmationDialogComponent.show({
      title: this.$t("projectConclusion.signOffAllDevices").toString(),
      message: this.$t("projectConclusion.signOffAllDevicesMessage").toString(),
    });

    if (result) {
      await this.signOffAllDevices();
    }
  }

  async onEditClientPanelAssignment(
    clientPanelAssignment: ClientPanelAssignment,
    clientPanel: ClientPanel,
    contactPerson: ContactPersonInfo,
    isReleasingAll?: boolean,
    shouldQueueClientPanels?: boolean
  ) {
    this.$emit(
      "onEditAssignment",
      clientPanelAssignment,
      clientPanel,
      contactPerson,
      isReleasingAll,
      shouldQueueClientPanels
    );
  }

  async signOffAllDevices() {
    this.isSiningOffDevices = true;
    const clientPanelsNotSignedOff = getIndependantDeviceProjectAssignments(this.clientPanelsNotSignedOff) as ClientPanelAssignment[];

    for (const clientPanelToSignOff of clientPanelsNotSignedOff) {
      const contactPersonInfo = this.projectDetails.clientData.contactPersons.find(
        (contactPerson) =>
          contactPerson.id === clientPanelToSignOff.contactPersonId
      );

      if (contactPersonInfo) {
        const clientPanel = await getClientPanel(
          clientPanelToSignOff.serialNumber
        );

        if (clientPanel) {
          await this.onEditClientPanelAssignment(
            clientPanelToSignOff,
            clientPanel,
            contactPersonInfo,
            true,
            true
          );
        }
      }
    }

    this.isSiningOffDevices = false;

    if (this.clientPanelsNotSignedOff.length == 0) {
      this.showAllDevicesSignedOffSnackbar = true;
    }
  }
}
